import { fetchWithAdmin } from './fetchHelper';

export interface TrendPoint {
  /** ISO prefix — 'YYYY-MM-DD' by day, 'YYYY-MM-DDTHH' by hour. */
  bucket: string;
  users: number;
  visits: number;
  events: number;
}
export interface RankedPath {
  path: string;
  entityId?: string;
  users: number;
  visits: number;
  events: number;
}

export interface SiteFunnel {
  visited: number;
  openedSimulation: number;
  /** Mixed at least once on a chemistry bench (recorded by the server). */
  usedLab: number;
  followedLink: number;
  signedIn: number;
}

export interface SitePathNode {
  name: string;
  visits: number;
  events: number;
  children: SitePathNode[];
}

export interface SiteAnalytics {
  from: string;
  to: string;
  trendUnit: 'hour' | 'day';
  trend: TrendPoint[];
  /** `users`: distinct accounts over the window — what the chart's Users line
   *  counts per day. Optional: a backend deployed before it sends none. */
  totals: { visits: number; users?: number; pageViews: number; simulationOpens: number; labUses: number };
  topPages: RankedPath[];
  topSimulations: RankedPath[];
  funnel: SiteFunnel;
  paths: SitePathNode;
  /** `kind` is the backend's `classifySource` label (`google`, `instagram`,
   *  `our-sites`, `other:<host>`…); absent from an older backend. */
  referrers: { referrerHost: string; visits: number; kind?: string }[];
  /** Which visits every number above describes. */
  audience: SiteAudience;
  /** How ALL the window's visits divide, regardless of `audience` — so the
   *  control can show what the selection was made from. */
  audienceSplit: { guest: number; account: number; none: number };
  /** Which science-site host every number describes: `all`, a host, or
   *  `unrecorded` — sessions from before the host was kept (2026-09-25). */
  host?: string;
  /** Sessions per host over the whole window, whatever `host` is. Absent
   *  from a backend deployed before the split. */
  hostSplit?: Record<string, number>;
  /** The values each session filter can take in this window, with how many
   *  sessions have each. Absent from a backend without session filters. */
  filterOptions?: Record<SessionFilterKey, { value: string; sessions: number }[]>;
  /** Visits carrying no activity tally, so present in NO figure on the page —
   *  not even `visited`. Shown as a caveat rather than absorbed. */
  unmeasuredVisits: number;
}

/** A visit is sorted by the identity it ENDED with: one that arrived as a
 *  guest and signed up mid-way counts as an account. `none` is a visit that
 *  never got an id at all — storage refused, or the guest could not be made. */
export type SiteAudience = 'all' | 'guest' | 'account' | 'none';

/** Session filters with values from the window (`filterOptions`). */
export type SessionFilterKey = 'referrer' | 'source' | 'campaign' | 'landing' | 'device';

/**
 * Everything that narrows the sessions a report describes: who, which host,
 * and the session filters (backend siteSessionFilters.ts). Empty or `all`
 * means not narrowed. The same set is sent to every tab that reads sessions.
 */
export interface SiteScope {
  who?: SiteAudience;
  host?: string;
  referrer?: string;
  source?: string;
  campaign?: string;
  landing?: string;
  device?: string;
  returning?: 'new' | 'returning';
  did?: 'simulation' | 'lab' | 'bounce';
}

const scopeQuery = (scope: SiteScope): string =>
  Object.entries(scope)
    .filter(([, v]) => v && v !== 'all')
    .map(([k, v]) => `&${k}=${encodeURIComponent(v as string)}`)
    .join('');

export const siteAnalyticsApi = {
  /** `from`/`to` are ISO instants. The picker works in the reader's local time;
   *  converting here keeps the server's contract in UTC, where the partition
   *  keys live. */
  get: (from: Date, to: Date, scope: SiteScope = {}) =>
    fetchWithAdmin<SiteAnalytics>(
      `/console/site-analytics?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`
      + scopeQuery(scope),
    ),
};

/** One row of a science-site funnel: how many of those who arrived reached each step. */
export interface ScienceSteps {
  arrived: number;
  openedSimulation: number;
  openedTwoSimulations: number;
  /** Mixed at least once on a chemistry bench (recorded by the server). */
  usedLab: number;
  signedUp: number;
  cameBack: number;
}

export interface AcquisitionFunnel {
  from: string;
  to: string;
  science: {
    total: ScienceSteps;
    /** `source` is a platform name, 'none', 'our-sites', or `other:<host>`. */
    bySource: ({ source: string } & ScienceSteps)[];
    returningPeople: number;
    excludedPeople: number;
    unidentifiedVisits: number;
  };
  links: {
    slug: string;
    placement: string;
    opened: number;
    becameGuest: number;
    answered: number;
    signedUp: number;
    cameBack: number;
  }[];
  conversions: { event: string; status: 'pending' | 'sent' | 'failed' | 'expired' | 'blocked'; n: number }[];
  /** Meta ad campaigns and ads, by the `utm_*` tags their URLs carried.
   *  No `campaign`: the ad's URL was not tagged. */
  campaigns: { campaign?: string; content?: string; clicks: number; secondSimulation: number; signedUp: number }[];
  /** Every account created in the window by its first touch — read from
   *  `users.acquisition`, so exact rather than inferred from visits. `source`
   *  is a campaign source, `link:<slug>`, a referrer host,
   *  `provisioned:<how>`, `direct`, or `(not recorded)` for an account from
   *  before capture. */
  /** `openedSimulation` is absent from a backend deployed before it. */
  sources: { source: string; property: string; accounts: number; openedSimulation?: number; answered: number; signedUp: number; cameBack: number }[];
}

export const acquisitionFunnelApi = {
  get: (from: Date, to: Date) =>
    fetchWithAdmin<AcquisitionFunnel>(
      `/console/acquisition-funnel?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`,
    ),
};

/** One landing page — see functions/src/admin/analytics/landings.ts. */
export interface LandingRow {
  page: string;
  /** Visits that began on this page. */
  visits: number;
  /** Of those, visits that were one page view and nothing else. */
  leftAfterOnePage: number;
  /** Distinct people with a visit that began here. */
  people: number;
  /** New people whose FIRST visit began here — the base of the step columns. */
  newPeople: number;
  openedSimulation: number;
  openedTwoSimulations: number;
  /** Mixed at least once on a chemistry bench (recorded by the server). */
  usedLab: number;
  signedUp: number;
  cameBack: number;
}

/** One session, raw — see `landingSessions` in functions/src/admin/analytics/landings.ts. */
export interface LandingSession {
  sessionId: string;
  /** A tester's or seeded account's session — hidden unless asked for. */
  internal?: true;
  /** Which property the session was on. Absent from an older backend, which
   *  sent science-site sessions only. */
  site?: 'science' | 'app';
  /** Answers submitted in the session (app sessions). */
  answers?: number;
  landedAt: string;
  landing: string;
  lastPage?: string;
  pageViews: number;
  durationMs?: number;
  /** mobile / tablet / desktop — absent on sessions from before it was stored. */
  device?: string;
  host?: string;
  referrerHost?: string;
  /** The referrer classified: instagram, google, none, our-sites, other:… */
  referrer: string;
  did: { simulation: boolean; lab: boolean; bounce: boolean };
  /** The published link THIS session landed from (not the person's first). */
  viaLink?: { slug: string; placement?: string; campaign?: string };
  /** `ios` | `android` | … — recorded from 27 Sep 2026. */
  os?: string;
  /** The app whose in-app browser the session ran in — `instagram`, `facebook`, … */
  inApp?: string;
  userId?: string;
  /** The account's state AT THIS SESSION: a guest before `signedUpAt`, signed in after. */
  account: 'guest' | 'signed-in' | 'none';
  /** The account's login id, else its email — absent for a guest. */
  userLabel?: string;
  /** When the guest became an account, and how — recorded from 28 Sep 2026. */
  signedUpAt?: string;
  signedUpVia?: string;
  returning?: 'new' | 'returning';
  /** The account's FIRST touch — not necessarily this session's. */
  firstTouch?: {
    source: string; medium?: string; campaign?: string; variant?: string;
    placement?: string; link?: string; adClick?: string; landingPath?: string; at?: string;
  };
}

export interface SiteLandings {
  from: string;
  to: string;
  rows: LandingRow[];
  totals: { visits: number; leftAfterOnePage: number; people: number; newPeople: number };
  excludedPeople: number;
  /** Every session the rows above are made of, newest first. Optional: a
   *  backend deployed before it sends none. */
  sessions?: LandingSession[];
  sessionsTruncated?: number;
}

export const siteLandingsApi = {
  /** Landings read sessions too, so they take the page's scope — less `who`,
   *  which that endpoint does not split by. */
  get: (from: Date, to: Date, scope: SiteScope = {}) =>
    fetchWithAdmin<SiteLandings>(
      `/console/site-landings?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`
      + scopeQuery({ ...scope, who: undefined }),
    ),
};

/** Every session on both properties, one row each — Acquisition → Sessions. */
/** One step of a session's journey — backend `admin/analytics/sessionJourney.ts`. */
export interface JourneyStep {
  /** Absent on a step rebuilt from the session's summary. */
  t?: string;
  kind: string;
  where?: string;
  n?: number;
  entities?: Record<string, number>;
  dwellMs?: number;
  props?: Record<string, string | number | boolean>;
}

export interface SessionJourney {
  sessionId: string;
  startedAt?: string;
  lastActivityAt?: string;
  arrival?: { at: string; slug?: string; placement?: string; campaign?: string };
  steps: JourneyStep[];
  /** The events have expired; the steps come from the session's summary. */
  fromSummary: boolean;
  /** The questions the steps name, as a person would recognise them. */
  questions?: Record<string, { text: string; subject?: string }>;
  /** The account: when it was made, and its sessions before this one. */
  account?: { createdAt?: string; signedUpAt?: string; signedUpVia?: string; priorSessions: number; priorIsFloor: boolean };
}

export const sessionsApi = {
  get: (from: Date, to: Date) =>
    fetchWithAdmin<{ from: string; to: string; sessions: LandingSession[]; sessionsTruncated?: number }>(
      `/console/sessions?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`,
    ),
  /** One session's acts in order. An app session is found by its account. */
  journey: (s: Pick<LandingSession, 'sessionId' | 'site' | 'userId'>) =>
    fetchWithAdmin<SessionJourney>(
      `/console/sessions/${encodeURIComponent(s.sessionId)}/journey?site=${s.site ?? 'science'}`
      + (s.userId ? `&userId=${encodeURIComponent(s.userId)}` : ''),
    ),
};

/** Users and sessions in the window across both sites — the line beside the
 *  window picker. */
export const siteVolumeApi = {
  get: (from: Date, to: Date) =>
    fetchWithAdmin<{
      users: number; sessions: number;
      bySite: Record<'science' | 'app', { users: number; sessions: number }>;
    }>(
      `/console/site-volume?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`,
    ),
};

/** Routes readers took — backend `admin/analytics/siteFlow.ts`. Node ids are
 *  `<step>:<page>`; `clamped` when the window reached past the 14 days the
 *  ordered events are kept. */
export interface SiteFlow {
  from: string;
  to: string;
  clamped: boolean;
  /** Which property was drawn — absent from a backend before the app's flow. */
  site?: 'science' | 'app';
  sessions: number;
  nodes: { id: string; step: number; page: string; sessions: number }[];
  links: { source: string; target: string; sessions: number }[];
}

/** `site: 'app'` reads the BuzzBrain app's screens, narrowed by the app
 *  report's person filters (its `who` included); otherwise the science site's
 *  pages, by its host and session filters. */
export const siteFlowApi = {
  get: (from: Date, to: Date, scope: Record<string, string> = {}, site: 'science' | 'app' = 'science') =>
    fetchWithAdmin<SiteFlow>(
      `/console/site-flow?from=${encodeURIComponent(from.toISOString())}`
      + `&to=${encodeURIComponent(to.toISOString())}`
      + (site === 'app' ? '&site=app' : '')
      + scopeQuery(site === 'app' ? scope as SiteScope : { ...(scope as SiteScope), who: undefined }),
    ),
};
